import React, { useEffect } from "react";
import { useState } from "react";
import isEmpty from "lodash/isEmpty";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { startLoader, stopLoader, formatDate } from "../../../libs/utils";
import { useTimezone } from "../../../contexts/TimezoneContext";
import Swal from "sweetalert2";
import {
  getBankUserInfo,
  getTransactionDetails,
  getTransactionReceipt,
} from "../../../services/user";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { REASON_CODES } from "../../../constants";
import handleWebhookDetails from "../../../components/webhookTabel";
import TransactionTimeline from "./TransactionTimeline";
import TxnPartnerSwitchTrail from "./TxnPartnerSwitchTrail";
import TxnActions from "./TxnActions";
import LabeledInput from "./LabeledInput";

function SearchTransaction() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const receiptEmail = searchParams.get("email")?.replace(/ /g, "+");
  const navigation = useNavigate();
  const { timezoneValue } = useTimezone();

  const [trackingId, setTrackingId] = useState("");
  const [bankUserInfo, setBankUserInfo] = useState({});
  const [fetchType, setFetchType] = useState("trackId"); // Default to trackId
  const BTN_STYLE = { height: "48px", marginLeft: "8px" };

  const {
    transaction_tracking_id,
    //transaction_ref_id,
    ach_initiated_time,
    ach_completed_time,
    clearing_account_transfer_time,
    transaction_current_status,
    remittance_transaction_strategy,
    // booked_fx_initiation_time,
    // partner_initiation_time,
    nium_transaction_initiate_time,
    nium_transaction_completed_time,
    nium_booked_amount,
    amount_usd,
    amount_inr,
    fx_rate,
    cash_in_status,
    beneficiary_name,
    account_number,
    ifsc_code,
    upi_id,
    account_type,
    paymentReasonCode,
    transaction_ref_id,
    email,
    user_email,
    userEmail,
    failure_reason,
    remittance_partner,
    nium_transaction_id,
    partner_switch_eligible,
    transfer_type = "",
    beneficiaryAccountType = "",
    mobile_number,
    transaction_info_list = [],
    time_line = [],
    last_wire_time,
    ach_partner,
    overall_eta,
    riskified_status = "",
    failure_or_cancellation_date,
    return_initiated_date,
    cashout_settled_date,
  } = bankUserInfo || {};
  useEffect(() => {
    if (id) {
      setTrackingId(id);
      searchUser(id);
    }
  }, [id]);

  const handleGoToUserSearch = () => {
    navigation(`/app/updated-search-users?email=${mobile_number}`);
  };

  const searchUser = (tid = "") => {
    startLoader();
    if (!trackingId && !tid) {
      stopLoader();
      Swal.fire({
        title: "Oops",
        text: "Please fill tracking Id",
        icon: "warning",
        confirmButtonText: "Ok",
      }).then((result) => {});
      return;
    }
    if (trackingId || tid) {
      startLoader();
      getTransactionDetails(trackingId || tid, fetchType)
        .then((response) => {
          stopLoader();
          if (response?.error) {
            setBankUserInfo({});
            Swal.fire({
              title: "Oops",
              text: response.error.message,
              icon: "warning",
              confirmButtonText: "Ok",
            }).then((result) => {});
            return;
          }
          if (response.success) {
            setBankUserInfo(response);
          }
        })
        .catch((error) => {
          stopLoader();
          setBankUserInfo({});
          Swal.fire({
            title: "Oops",
            text: error.data?.error?.message,
            icon: "warning",
            confirmButtonText: "Ok",
          }).then((result) => {});
          return;
        });
    }
  };

  const handleWebhookDetail = async (documentId) => {
    handleWebhookDetails(documentId);
  };

  const downloadReceiptUrl = (receiptDownloadUrl) => {
    const link = document.createElement("a");
    link.href = receiptDownloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getReceiptUserEmail = async () => {
    const userIdentifier =
      receiptEmail ||
      email ||
      user_email ||
      userEmail ||
      bankUserInfo?.user?.email;

    if (userIdentifier) {
      return userIdentifier;
    }

    if (!mobile_number) {
      return "";
    }

    const response = await getBankUserInfo(mobile_number);
    if (response?.error) {
      throw new Error(response.error.message);
    }

    return response?.user?.email || "";
  };

  const handleDownloadReceipt = async () => {
    if (!transaction_ref_id) {
      Swal.fire({
        title: "Oops",
        text: "Receipt download details are missing",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    try {
      const userIdentifier = await getReceiptUserEmail();

      if (!userIdentifier) {
        Swal.fire({
          title: "Oops",
          text: "Receipt download details are missing",
          icon: "warning",
          confirmButtonText: "Ok",
        });
        return;
      }

      const response = await getTransactionReceipt(
        userIdentifier,
        transaction_ref_id,
      );
      const { error, receiptDownloadUrl } = response || {};

      if (error) {
        Swal.fire({
          title: "Oops",
          text: error.message,
          icon: "warning",
          confirmButtonText: "Ok",
        });
        return;
      }

      if (receiptDownloadUrl) {
        downloadReceiptUrl(receiptDownloadUrl);
        return;
      }

      Swal.fire({
        title: "Oops",
        text: "Receipt is not available for this transaction",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    } catch (error) {
      Swal.fire({
        title: "Oops",
        text:
          error.data?.error?.message ||
          error.message ||
          "Unable to download receipt",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }
  };

  useEffect(() => {
    if (bankUserInfo) {
      setBankUserInfo(bankUserInfo);
    }
  }, [bankUserInfo]);

  const handleSearchByChange = (e) => {
    setTrackingId("");
    setFetchType(e.target.value);
  };

  return (
    <>
      <Box sx={{ ml: "4", pt: "14 !important", pb: "5", px: 3 }}>
        {id && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "25px",
              marginTop: "20px",
              gap: "16px",
            }}
          >
            <Link to={-1}>
              <ArrowBackIcon
                fontSize="large"
                color="secondary"
                sx={{ background: "#040721", p: 1, borderRadius: "50%" }}
              />
            </Link>
            <Typography
              variant="h5"
              component="h5"
              fontWeight={"bold"}
              color={"#3d3d3d"}
            >
              Transaction Tracking ID ({id})
            </Typography>
          </div>
        )}
        {!id && (
          <>
            <Typography
              component="h4"
              variant="h4"
              color="inherit"
              noWrap
              sx={{ mb: 2, fontSize: "22px", fontWeight: "700" }}
            >
              Search Tracking Id
            </Typography>

            <div className="searchUserHeader mb-5">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <FormControl variant="standard" sx={{ minWidth: 220 }}>
                  <InputLabel id="fetch-type-label">Search By</InputLabel>
                  <Select
                    labelId="fetch-type-label"
                    id="fetch-type-select"
                    value={fetchType}
                    onChange={handleSearchByChange}
                    label="Search By"
                  >
                    <MenuItem value="trackId">TRACKING_ID</MenuItem>
                    <MenuItem value="partnerTxnId">
                      PARTNER_TRANSACTION_ID
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  style={{ width: "280px" }}
                  label={
                    fetchType === "trackId"
                      ? "Tracking ID"
                      : "Partner Transaction ID"
                  }
                  variant="standard"
                  value={trackingId || ""}
                  onChange={(e) => setTrackingId(e.target.value)}
                />
                <Button
                  onClick={() => searchUser()}
                  style={BTN_STYLE}
                  variant="contained"
                >
                  Submit
                </Button>
                {mobile_number && (
                  <Button
                    onClick={handleGoToUserSearch}
                    style={BTN_STYLE}
                    variant="contained"
                  >
                    Go To User Search
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {!isEmpty(bankUserInfo) && (
          <Box sx={{ background: "#ffffff", p: 2, mb: 4, pb: 4 }}>
            <div className="row">
              <div className="col-md-12">
                <LabeledInput
                  label="Transaction Completion Estimated Time"
                  value={formatDate(overall_eta, timezoneValue)}
                />
              </div>
            </div>
            <div className="row ">
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="Tracking Id"
                    value={transaction_tracking_id}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="Transaction Current Status"
                    value={transaction_current_status}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="ACH initiated Date"
                    value={formatDate(ach_initiated_time, timezoneValue)}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="ACH completed Date"
                    value={formatDate(ach_completed_time, timezoneValue)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="Clearing transferred Date"
                    value={formatDate(
                      clearing_account_transfer_time,
                      timezoneValue,
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="NIUM Booked INR"
                    value={nium_booked_amount}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput label="Cash In Status" value={cash_in_status} />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="Remittance Transaction Strategy"
                    value={remittance_transaction_strategy}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="Name of Beneficiary"
                    value={beneficiary_name}
                  />
                </div>
                {upi_id && (
                  <div className="col-md-6">
                    <LabeledInput label="UPI ID" value={upi_id} />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="Transfer Reason code"
                    value={paymentReasonCode}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="Transfer Reason"
                    value={REASON_CODES[paymentReasonCode]}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput label="FX Rate" value={fx_rate} />
                </div>

                <div className="col-md-6">
                  <LabeledInput label="Account Type" value={account_type} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="NIUM trxn Inititated Date"
                    value={formatDate(
                      nium_transaction_initiate_time,
                      timezoneValue,
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="Txn Paid Date"
                    value={formatDate(
                      nium_transaction_completed_time,
                      timezoneValue,
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <LabeledInput label="USD Amount" value={amount_usd} />
                </div>
                <div className="col-md-6">
                  <LabeledInput label="INR Amount" value={amount_inr} />
                </div>
                {account_number && (
                  <div className="col-md-6">
                    <LabeledInput
                      label="A/c No. (last 4)"
                      value={account_number}
                    />
                  </div>
                )}
                {ifsc_code && (
                  <div className="col-md-6">
                    <LabeledInput label="IFSC Code" value={ifsc_code} />
                  </div>
                )}
                {beneficiaryAccountType && (
                  <div className="col-md-6">
                    <LabeledInput
                      label="Bank account type"
                      value={beneficiaryAccountType}
                    />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-6">
                  <LabeledInput
                    label="Settlement Partner"
                    value={remittance_partner}
                  />
                </div>
                <div className="col-md-6">
                  <LabeledInput
                    label="Banking Partner ID"
                    value={nium_transaction_id || ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4">
                  <LabeledInput label="Transfer Type" value={transfer_type} />
                </div>
                <div className="col-md-4">
                  <LabeledInput label="Ach Partner" value={ach_partner} />
                </div>
                {riskified_status && (
                  <div className="col-md-4">
                    <LabeledInput
                      label="Riskified Status"
                      value={riskified_status}
                    />
                  </div>
                )}
              </div>
              {failure_reason && (
                <div className="row">
                  <div className="col-md-12">
                    <LabeledInput
                      label="Failure Reason"
                      value={failure_reason}
                      type="decimal"
                    />
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-md-4">
                  <LabeledInput
                    label="Failure/Cancellation Date"
                    value={formatDate(
                      failure_or_cancellation_date,
                      timezoneValue,
                    )}
                  />
                </div>
                <div className="col-md-4">
                  <LabeledInput
                    label="Cashout Date"
                    value={formatDate(cashout_settled_date, timezoneValue)}
                  />
                </div>
                <div className="col-md-4">
                  <LabeledInput
                    label="Return Initiation Date"
                    value={formatDate(return_initiated_date, timezoneValue)}
                  />
                </div>
              </div>

              <TxnPartnerSwitchTrail
                transaction_info_list={transaction_info_list}
                handleWebhookDetail={handleWebhookDetail}
              />

              <TransactionTimeline timeline={time_line} />

              {last_wire_time && (
                <>
                  <div className="lineBreak" />
                  <div className="mt15">
                    <span className="heading">Last Wire time:</span>
                    <span className="lastwiretime">
                      {formatDate(last_wire_time, timezoneValue)}
                    </span>
                  </div>
                </>
              )}

              <div className="row mt15">
                <div className="col-md-12 mb-3">
                  <Button onClick={handleDownloadReceipt} variant="contained">
                    Download Receipt
                  </Button>
                </div>
                <TxnActions
                  searchUser={searchUser}
                  transaction_ref_id={transaction_ref_id}
                  setBankUserInfo={setBankUserInfo}
                  partner_switch_eligible={partner_switch_eligible}
                  remittance_partner={remittance_partner}
                />
              </div>
            </div>
          </Box>
        )}
      </Box>
    </>
  );
}

export default SearchTransaction;
